import React, { useState, useEffect, useCallback } from "react";
import * as XLSX from 'xlsx';
import { Grid, GridColumn, GridSortChangeEvent, GridFilterChangeEvent, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { SortDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Switch } from "@progress/kendo-react-inputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import {
  FaFileExcel,
  FaCheckCircle,
  FaTimesCircle,
  FaPlusCircle,
  FaStickyNote,
} from "react-icons/fa";

import "./Grids.css";
import { enquiryGrid } from "../../data/interfaces";
import { DateFormatter } from "../../data/helper";
import { 
  getEnquiriesWithPagination,
  getEnquiriesForEnquiriesFilterDropdown,
  getClientsForEnquiriesFilterDropdown,
  getSitesForEnquiriesFilterDropdown,
  getCreatedByForEnquiriesFilterDropdown,
  getAssignedToForEnquiriesFilterDropdown,
} from "../../data/enquiryData";
import { useSessionState } from "../../helpers/useSessionState";

const formatBool = (val: boolean | null | undefined): string =>
  val === true ? "YES" : val === false ? "NO" : "";

// Custom filter cell for enquiry ref column with dropdown
const EnquiryRefFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = React.useState(props.data || []);
  const [loading, setLoading] = React.useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      const enquiriesData = await getEnquiriesForEnquiriesFilterDropdown(filter);
      const enquiryRefs = enquiriesData.map((e: any) => e.text).sort();
      setFilteredData(enquiryRefs);
    } catch (error) {
      console.error('Error loading filtered enquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by enquiry..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

// Custom filter cell for client column with dropdown
const ClientFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = React.useState(props.data || []);
  const [loading, setLoading] = React.useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      const clientsData = await getClientsForEnquiriesFilterDropdown(filter);
      const clientNames = clientsData.map((c: any) => c.text).sort();
      setFilteredData(clientNames);
    } catch (error) {
      console.error('Error loading filtered clients:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by client..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

// Custom filter cell for site column with dropdown
const SiteFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = React.useState(props.data || []);
  const [loading, setLoading] = React.useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      const sitesData = await getSitesForEnquiriesFilterDropdown(filter);
      const siteNames = sitesData.map((s: any) => s.text).sort();
      setFilteredData(siteNames);
    } catch (error) {
      console.error('Error loading filtered sites:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by site..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

// Custom filter cell for Created By column with dropdown
const CreatedByFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      const createdByData = await getCreatedByForEnquiriesFilterDropdown(filter);
      const createdByNames = createdByData.map((e: any) => e.text).sort();
      setFilteredData(createdByNames);
    } catch (error) {
      console.error('Error loading created by:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by created by..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

// Custom filter cell for Assigned To column with dropdown
const AssignedToFilterCell = (props: any) => {
  const [filteredData, setFilteredData] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  
  const hasValue = (value: any) => Boolean(value && value !== '');
  
  const onChange = (event: any) => {
    const value = event.target.value;
    props.onChange({
      value: hasValue(value) ? value : '',
      operator: hasValue(value) ? 'contains' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onFilterChange = async (event: any) => {
    const filter = event.filter.value;
    
    try {
      setLoading(true);
      const assignedToData = await getAssignedToForEnquiriesFilterDropdown(filter);
      const assignedToNames = assignedToData.map((a: any) => a.text).sort();
      setFilteredData(assignedToNames);
    } catch (error) {
      console.error('Error loading assigned to:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setFilteredData(props.data || []);
  }, [props.data]);

  return (
    <ComboBox
      data={filteredData}
      onChange={onChange}
      onFilterChange={onFilterChange}
      value={props.value || ''}
      placeholder="Filter by assigned to..."
      filterable={true}
      allowCustom={true}
      loading={loading}
      className="k-filtercell-dropdown"
    />
  );
};

function EnquiriesGrid(props: any) {
  const dateFormatter = new DateFormatter();
  
  // State for Kendo Grid
  const [data, setData] = useState<enquiryGrid[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Pagination state (page size persists, page position resets on re-entry)
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useSessionState("enquiries.take", 20);

  // Sorting state
  const [sort, setSort] = useSessionState<SortDescriptor[]>("enquiries.sort", []);

  // Filtering state
  const [filter, setFilter] = useSessionState<CompositeFilterDescriptor | undefined>("enquiries.filter", undefined);

  // Toggle switches
  const [showDead, setShowDead] = useSessionState("enquiries.showDead", false);
  const [showProgressed, setShowProgressed] = useSessionState("enquiries.showProgressed", false);

  const handleShowDead = (event: any) => {
    setShowDead(event.target.value);
    if (props.onShowDead) {
      props.onShowDead(event.target.value);
    }
  };

  const handleShowProgressed = (event: any) => {
    setShowProgressed(event.target.value);
    if (props.onShowProgressed) {
      props.onShowProgressed(event.target.value);
    }
  };

  // Load data function
  const loadData = useCallback(async () => {
    setLoading(true);
    console.log('Loading data with:', { skip, take, showDead, showProgressed });
    try {
      const result = await getEnquiriesWithPagination({
        skip,
        take,
        sort: sort.map(s => ({ field: s.field!, dir: s.dir! })),
        filter,
        showDead,
        showProgressed,
      });
      
      console.log('Data loaded:', { dataCount: result.data.length, total: result.total });
      setData(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error('Error loading enquiries:', error);
    } finally {
      setLoading(false);
    }
  }, [skip, take, sort, filter, showDead, showProgressed]);

  // Load data when dependencies change
  useEffect(() => {
    loadData();
  }, [loadData, props.refreshKey]);

  // Handle page change
  const handlePageChange = (event: GridPageChangeEvent) => {
    console.log('Page change:', event.page);
    setSkip(event.page.skip);
    setTake(event.page.take);
  };

  // Handle sort change
  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
    setSkip(0); // Reset to first page when sorting changes
  };

  // Handle filter change
  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter);
  };

  const exportToExcel = () => {
    const exportData = data.map(base => {
      const full = (props.onExportData as any[])?.find(e => e.enquiryId === base.enquiryId) || {};
      return {
        ...base,
        note1Details: full.note1Details || "",
        note2Details: full.note2Details || "",
        note3Details: full.note3Details || "",
        note1Completed: formatBool(full.note1Completed),
        note2Completed: formatBool(full.note2Completed),
        note3Completed: formatBool(full.note3Completed),
      };
    });

    const headers = [
      "enquiryId", "ref", "refFormatted", "description", "clientId", "clientName", 
      "siteId", "siteAddr", "employeeCreatedById", "employeeCreatedByName", 
      "employeeAssignedToId", "employeeAssignedToName", "createdDate", "minsSinceCreation", 
      "deadDate", "progressed", "noteCount", "isUnassignedReferral", 
      "note1Details", "note1Completed", "note2Details", "note2Completed", 
      "note3Details", "note3Completed"
    ];
    
    const ws = XLSX.utils.json_to_sheet(exportData, { header: headers });
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
    const dateStr = dateFormatter.format(new Date().toDateString());
    XLSX.writeFile(wb, `enquiries_${dateStr}.xlsx`);
  };

  // Row render with conditional formatting
  const rowRender = (tr: React.ReactElement, rowProps: any) => {
    const row = rowProps.dataItem;
    
    const bgColor = () => {
      // Dead enquiries - red/pink
      if (row.deadDate) {
        return "rgba(235, 199, 199, 0.5)";
      } 
      // High priority - orange (same as jobs with isBooked=false)
      else if (row.highPriority) {
        return "rgba(233, 84, 13, 0.3)";
      } 
      // Progressed - green
      else if (row.progressed) {
        return "#d7edd7";
      } 
      // Unassigned referral - light orange
      else if (row.isUnassignedReferral) {
        return "rgba(233, 83, 13, 0.2)";
      } 
      // Default - light blue
      else {
        return "rgb(245 249 251)";
      }
    };

    const textColor = row.deadDate ? "rgb(137 5 5)" : "#1e2a4e";
    
    // Add border for high priority items for extra visibility
    const borderStyle = row.highPriority && !row.deadDate 
      ? { borderLeft: "4px solid #e9540d" }
      : {};

    return React.cloneElement(tr, {
      ...tr.props,
      style: {
        ...tr.props.style,
        backgroundColor: bgColor(),
        color: textColor,
        cursor: 'pointer',
        ...borderStyle,
      },
      onDoubleClick: () => {
        if (props.onEditToggle) {
          props.onEditToggle(rowProps.dataItem.enquiryId);
        }
      },
    }, tr.props.children);
  };

  // Custom cell renderer for refFormatted column
  const RefFormattedCell = (cellProps: any) => {
    const row = cellProps.dataItem;

    return (
      <td>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <span
            style={{
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: "5px"
            }}
          >
            {row.refFormatted}
            {row.noteCount > 0 && <FaStickyNote />}
          </span>
        </div>
      </td>
    );
  };

  // Custom cell renderer for site column
  const SiteCell = (cellProps: any) => (
    <td>
      <Tooltip anchorElement="target" position="top">
        <span title={cellProps.dataItem.siteAddr} className="overflow__container">
          {cellProps.dataItem.siteAddr}
        </span>
      </Tooltip>
    </td>
  );

  // Custom cell renderer for description column
  const DescriptionCell = (cellProps: any) => (
    <td>
      <Tooltip anchorElement="target" position="top">
        <span title={cellProps.dataItem.description} className="overflow__container">
          {cellProps.dataItem.description}
        </span>
      </Tooltip>
    </td>
  );

  // Custom cell renderer for created time with priority circle
  const CreatedTimeCell = (cellProps: any) => {
    const { minsSinceCreation, createdDate } = cellProps.dataItem;
    let color;
    if (minsSinceCreation >= 0 && minsSinceCreation <= 30) {
      color = "3";
    } else if (minsSinceCreation > 30 && minsSinceCreation <= 90) {
      color = "1";
    } else if (minsSinceCreation > 90) {
      color = "2";
    }

    return (
      <td>
        <div className="priority-cell">
          <div className={`circle__${color}`}>
            {createdDate}
          </div>
        </div>
      </td>
    );
  };

  // Custom cell renderer for due date
  const DueDateCell = (cellProps: any) => (
    <td>
      {cellProps.dataItem.dueDate ? cellProps.dataItem.dueDate : "-"}
    </td>
  );

  return (
    <div className="p-2">
      {/* Header */}
      <div className="grid__title__container" style={{ marginLeft: '-1rem', marginRight: '-1rem' }}>
        <div className="grid__header__background">
          <div className="grid__title">
            <div style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "1rem"
            }}>
              <div>Enquiries</div>
              
              <div style={{ display: "flex", alignItems: "center" }}>
                <label style={{ marginRight: "0.5rem", fontSize: "0.9rem" }}>Show Dead:</label>
                <Switch
                  className="grid__switch"
                  size={"large"}
                  onChange={handleShowDead}
                  checked={showDead}
                />
                <Tooltip anchorElement="target" position="top" openDelay={0}>
                  <FaTimesCircle
                    style={{
                      fontSize: "14pt",
                      marginLeft: "0.7rem",
                      color: showDead ? "red" : "rgba(255, 255, 255, 0.5)",
                    }}
                    title="Show Dead Enquiries"
                  />
                </Tooltip>
              </div>
              
              <div style={{ display: "flex", alignItems: "center" }}>
                <label style={{ marginRight: "0.5rem", fontSize: "0.9rem" }}>Show Progressed:</label>
                <Switch
                  className="grid__switch"
                  size={"large"}
                  onChange={handleShowProgressed}
                  checked={showProgressed}
                />
                <Tooltip anchorElement="target" position="top" openDelay={0}>
                  <FaCheckCircle
                    style={{
                      fontSize: "14pt",
                      marginLeft: "0.7rem",
                      color: showProgressed ? "green" : "rgba(255, 255, 255, 0.5)",
                    }}
                    title="Show Progressed Enquiries"
                  />
                </Tooltip>
              </div>
            </div>     
       
            <div style={{display: "flex", flexDirection: "row"}}>
              {props.lockEdit === false && props.onAddToggle && (
                <div onClick={() => props.onAddToggle()} className="grid__addrecord__button">
                  <FaPlusCircle style={{ fontSize: "14pt", marginRight: "1rem" }} />
                  <p style={{ margin: 0 }}>Add new enquiry</p>
                </div>
              )}

              <div onClick={exportToExcel} className="grid__export__button">
                <FaFileExcel style={{ fontSize: "14pt", marginRight: "1rem" }} />
                <p style={{ margin: 0 }}>Export enquiries</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-2" />

      {/* Grid Container with Loading Overlay */}
      <div style={{ position: "relative" }}>
        {/* Loading Indicator Overlay */}
        {loading && (
          <div style={{ 
            position: "absolute", 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: "rgba(255, 255, 255, 0.8)", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            zIndex: 1000,
            borderRadius: "8px"
          }}>
            <div style={{ textAlign: "center" }}>
              <div className="spinner" />
              <div style={{ marginTop: "1rem", color: "#1e2a4e", fontWeight: 500 }}>
                Loading enquiries...
              </div>
            </div>
          </div>
        )}

        {/* Grid */}
        <Grid
          data={data}
          total={total}
          skip={skip}
          take={take}
          pageable={{
            buttonCount: 5,
            info: true,
            type: 'numeric',
            pageSizes: [10, 20, 50, 100],
            previousNext: true,
          }}
          sortable
          sort={sort}
          onSortChange={handleSortChange}
          filterable
          filter={filter}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          rowRender={rowRender}
          resizable={true}
          style={{ height: 'calc(100vh - 300px)', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}
        >
          <GridColumn 
            field="refFormatted" 
            title="Enquiry Ref" 
            width="180px"
            cell={RefFormattedCell}
            filterCell={EnquiryRefFilterCell}
          />
          <GridColumn 
            field="clientName" 
            title="Client" 
            width="200px"
            filterCell={ClientFilterCell}
          />
          <GridColumn 
            field="siteAddr" 
            title="Site" 
            width="250px"
            cell={SiteCell}
            filterCell={SiteFilterCell}
          />
          <GridColumn 
            field="description" 
            title="Description" 
            width="300px"
            cell={DescriptionCell}
          />
          <GridColumn 
            field="employeeCreatedByName" 
            title="Created by" 
            width="180px"
            filterCell={CreatedByFilterCell}
          />
          <GridColumn 
            field="createdDate" 
            title="Created time" 
            width="180px"
            cell={CreatedTimeCell}
            filterable={false}
          />
          <GridColumn 
            field="employeeAssignedToName" 
            title="Assigned to" 
            width="180px"
            filterCell={AssignedToFilterCell}
          />
          <GridColumn 
            field="dueDate" 
            title="Due Date" 
            width="150px"
            cell={DueDateCell}
          />
        </Grid>
      </div>
    </div>
  );
}

export default EnquiriesGrid;
